import { useState, useEffect } from 'react';
import { Brain, Scan } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fetchCoachSummary } from '@/lib/api';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import type { TrainingLoad } from '@/types';

interface InsightsCoachPanelProps {
  data: TrainingLoad[];
  className?: string;
}

/** Typewriter text that reveals character by character */
function TypewriterText({ text, speed = 18 }: { text: string; speed?: number }) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    setDisplayed('');
    setDone(false);
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(interval);
        setDone(true);
      }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);

  return (
    <span>
      {displayed}
      {!done && <span className="inline-block w-[2px] h-3 bg-[hsl(170,80%,45%)] ml-0.5 animate-pulse" />}
    </span>
  );
}

export function InsightsCoachPanel({ data, className }: InsightsCoachPanelProps) {
  const [scanPhase, setScanPhase] = useState<'scanning' | 'complete'>('scanning');

  // Fetch coach summary from API
  const { data: coachData } = useAuthenticatedQuery({
    queryKey: ['coach', 'summary'],
    queryFn: fetchCoachSummary,
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });

  // Scan animation
  useEffect(() => {
    const timer = setTimeout(() => setScanPhase('complete'), 1800);
    return () => clearTimeout(timer);
  }, [coachData?.summary]);

  const displayText = coachData?.summary || 'Translating miles into momentum… coach insight loading';
  const currentState = coachData?.current_state || 'Analyzing';
  const nextFocus = coachData?.next_focus || 'Stand by for coach focus.';

  return (
    <div className={cn(
      'rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px] overflow-hidden',
      'shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]',
      className
    )}>
      {/* Scanning line overlay */}
      {scanPhase === 'scanning' && (
        <div className="absolute inset-0 pointer-events-none z-10">
          <div 
            className="h-px w-full bg-gradient-to-r from-transparent via-[hsl(170,80%,45%)] to-transparent opacity-60"
            style={{
              animation: 'scanLine 1.8s ease-in-out',
            }}
          />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-white/[0.06]">
        <div className="p-1.5 rounded-md bg-[rgba(0,255,200,0.08)]">
          <Brain className="h-3.5 w-3.5 text-[hsl(170,80%,45%)]" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground">
            COACH DIAGNOSTIC
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {scanPhase === 'scanning' ? (
            <>
              <Scan className="h-3 w-3 text-[hsl(170,80%,45%)] animate-pulse" strokeWidth={1.5} />
              <span className="text-[9px] font-mono text-[hsl(170,80%,45%)]">SCANNING</span>
            </>
          ) : (
            <>
              <div className="w-1.5 h-1.5 rounded-full bg-[hsl(170,80%,45%)]" />
              <span className="text-[9px] font-mono text-[hsl(170,80%,45%)]">COMPLETE</span>
            </>
          )}
        </div>
      </div>

      {/* Analysis body */}
      <div className="p-4">
        <p className="text-sm leading-relaxed text-[#c8c8c8] font-mono">
          {scanPhase === 'complete' ? (
            <TypewriterText text={displayText} speed={12} />
          ) : (
            <span className="text-muted-foreground">Coach brain in tempo mode...</span>
          )}
        </p>
      </div>

      {/* Status footer */}
      <div className="flex items-center gap-4 px-4 py-3 border-t border-white/[0.06]">
        <div>
          <span className="text-[9px] font-mono tracking-wider uppercase text-muted-foreground block">STATE</span>
          <span className="text-xs font-mono text-[hsl(170,80%,45%)]">{currentState}</span>
        </div>
        <div className="w-px h-6 bg-white/[0.06]" />
        <div>
          <span className="text-[9px] font-mono tracking-wider uppercase text-muted-foreground block">NEXT FOCUS</span>
          <span className="text-xs font-mono text-foreground">{nextFocus}</span>
        </div>
      </div>

      {/* CSS for scan animation */}
      <style>{`
        @keyframes scanLine {
          0% { transform: translateY(0); opacity: 0; }
          10% { opacity: 0.6; }
          100% { transform: translateY(200px); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
